import type React from "react";
import { useState } from "react";
import { ChevronDown, Loader2, Maximize, Minimize } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { i18n } from "@frontend/i18n";
import { EventCredits } from "../components/EventCredits";
import { PlantaLockup } from "../components/PlantaLockup";
import { WhatsAppGlyph } from "../components/WhatsAppGlyph";
import { Ltf2026OnboardingPage } from "@/features/ltf-onboarding/presentation/pages/Ltf2026OnboardingPage";
import { useEventLandingController } from "../controllers/useEventLandingController";

const STEP_KEYS = [
  "user.eventLanding.steps.tellPorter",
  "user.eventLanding.steps.suggestions",
  "user.eventLanding.steps.connect",
] as const;

// Illustrative — the public event view exposes no participants, so the row
// reuses the bundled directory demo set rather than implying real attendees.
const FACE_SRCS = [
  "/assets/demo-photos/t1-02.webp",
  "/assets/demo-photos/t2-01.webp",
  "/assets/demo-photos/t2-04.webp",
  "/assets/demo-photos/t4-08.webp",
  "/assets/demo-photos/t3-10.webp",
  "/assets/demo-photos/t4-09.webp",
] as const;

const LEGAL_PRIVACY_SENTINEL = "\x00PRIVACY\x00";
const LEGAL_TERMS_SENTINEL = "\x00TERMS\x00";

function renderLegalBody({
  privacyHref,
  termsHref,
}: {
  readonly privacyHref: string;
  readonly termsHref: string;
}): React.ReactNode {
  const privacyLink = (
    <a
      key="privacy"
      href={privacyHref}
      className="whitespace-nowrap underline"
      target="_blank"
      rel="noopener"
    >
      {i18n.t("user.eventLanding.legal.privacyLabel")}
    </a>
  );
  const termsLink = (
    <a
      key="terms"
      href={termsHref}
      className="whitespace-nowrap underline"
      target="_blank"
      rel="noopener"
    >
      {i18n.t("user.eventLanding.legal.termsLabel")}
    </a>
  );

  const raw = i18n.t("user.eventLanding.legal.body", {
    privacy: LEGAL_PRIVACY_SENTINEL,
    terms: LEGAL_TERMS_SENTINEL,
  });
  const nodes: React.ReactNode[] = [];
  for (const privacyChunk of raw.split(LEGAL_PRIVACY_SENTINEL)) {
    if (nodes.length > 0) nodes.push(privacyLink);
    const termsParts = privacyChunk.split(LEGAL_TERMS_SENTINEL);
    termsParts.forEach((part, i) => {
      if (i > 0) nodes.push(termsLink);
      nodes.push(part);
    });
  }
  return nodes;
}

function JoinFoot({
  privacyHref,
  termsHref,
  eventName,
  logoUrl,
  hasLogo,
  onLogoError,
  sponsorLogoSrc,
  sponsorName,
  hasSponsor,
  onSponsorError,
}: {
  readonly privacyHref: string;
  readonly termsHref: string;
  readonly eventName: string;
  readonly logoUrl: string | null;
  readonly hasLogo: boolean;
  readonly onLogoError: () => void;
  readonly sponsorLogoSrc: string | null;
  readonly sponsorName: string;
  readonly hasSponsor: boolean;
  readonly onSponsorError: () => void;
}): React.JSX.Element {
  const hasCredit = hasLogo || hasSponsor;

  return (
    <footer
      className={
        hasCredit
          ? "sticky bottom-0 z-10 mx-auto mt-auto flex w-full max-w-[390px] flex-col items-center gap-2 border-t border-border bg-card px-8 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 lg:max-w-none lg:static lg:flex-row lg:justify-between lg:gap-6 lg:px-12 lg:py-3 lg:pb-3 [@media(max-height:640px)]:pb-[calc(0.75rem+env(safe-area-inset-bottom))] [@media(max-height:640px)]:pt-3"
          : "sticky bottom-0 z-10 mx-auto mt-auto hidden w-full max-w-[390px] flex-col items-center gap-2 border-t border-border bg-card px-8 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 lg:max-w-none lg:static lg:flex lg:flex-row lg:justify-between lg:gap-6 lg:px-12 lg:py-3 lg:pb-3 [@media(max-height:640px)]:pb-[calc(0.75rem+env(safe-area-inset-bottom))] [@media(max-height:640px)]:pt-3"
      }
    >
      <p className="hidden max-w-[420px] text-pretty text-[11.5px] leading-[1.5] text-muted-foreground lg:block">
        {i18n.t("user.eventLanding.legal.intro")}
        <br />
        {renderLegalBody({
          privacyHref,
          termsHref,
        })}
      </p>
      <EventCredits
        eventName={eventName}
        logoUrl={logoUrl}
        hasLogo={hasLogo}
        onLogoError={onLogoError}
        sponsorLogoSrc={sponsorLogoSrc}
        sponsorName={sponsorName}
        hasSponsor={hasSponsor}
        onSponsorError={onSponsorError}
      />
    </footer>
  );
}

function EventKeyVisual({
  src,
  onError,
}: {
  readonly src: string | null;
  readonly onError: () => void;
}): React.JSX.Element | null {
  if (src === null) return null;

  // Decorative: the eyebrow and the organizer credit already name the event.
  //
  // Never withdrawn. On a short viewport it scales with the viewport height
  // and the page tightens around it, so a phone with the URL bar open still
  // shows the host's image; hiding it (PLA-1755) lost the host's own picture
  // where most people land. svh, not vh: on iOS Safari vh is the
  // toolbar-collapsed height, so a vh band would stay tall while the bar is
  // open. The 48px floor under 640 keeps the CTA above the sticky foot on an
  // SE with the URL bar open (600 tall); with both bars (560) the page
  // scrolls about 100px and the CTA stays reachable.
  //
  // On a wide card the band is whatever the viewport leaves after the card's
  // fixed content (636px at lg, measured: root padding + main + foot; if lg
  // copy or spacing grows, re-measure it, the cost is a small scroll), capped
  // at the phone frame's 3.75:1 — 245px at 918 wide. A fixed 104px there would crop away over half of the image, and
  // a host designs one image for one shape. When it does crop, the window sits
  // low: a key visual carries its title in its lower part (the Canary wordmark
  // row), and the top edge is where a small lockup lives, the cheapest loss.
  return (
    <img
      src={src}
      alt=""
      onError={onError}
      className="h-[clamp(64px,14svh,104px)] w-full shrink-0 object-cover lg:object-[center_85%] lg:h-[clamp(104px,100svh-636px,245px)] max-lg:[@media(max-height:640px)]:h-12"
    />
  );
}

function TvCredits({
  organizedByLabel,
  supportedByLabel,
  eventName,
  logoUrl,
  onLogoError,
  sponsorLogoSrc,
  sponsorName,
  onSponsorError,
}: {
  readonly organizedByLabel: string;
  readonly supportedByLabel: string;
  readonly eventName: string;
  readonly logoUrl: string | null;
  readonly onLogoError: () => void;
  readonly sponsorLogoSrc: string | null;
  readonly sponsorName: string;
  readonly onSponsorError: () => void;
}): React.JSX.Element | null {
  if (logoUrl === null && sponsorLogoSrc === null) return null;

  return (
    <div className="mt-[6vh] flex items-center gap-[4vw]">
      {logoUrl !== null && (
        <TvCredit
          label={organizedByLabel}
          src={logoUrl}
          alt={eventName}
          onError={onLogoError}
        />
      )}
      {sponsorLogoSrc !== null && (
        <TvCredit
          label={supportedByLabel}
          src={sponsorLogoSrc}
          alt={sponsorName}
          onError={onSponsorError}
        />
      )}
    </div>
  );
}

function TvCredit({
  label,
  src,
  alt,
  onError,
}: {
  readonly label: string;
  readonly src: string;
  readonly alt: string;
  readonly onError: () => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <p className="text-[clamp(11px,1vw,15px)] text-primary-foreground/65">
        {label}
      </p>
      <div className="rounded-lg bg-white px-3 py-2">
        <img
          src={src}
          alt={alt}
          onError={onError}
          className="max-h-[clamp(33px,3.7vw,53px)] max-w-[clamp(126px,14vw,202px)] w-auto object-contain"
        />
      </div>
    </div>
  );
}

interface EventLandingPageProps {
  readonly eventIdPublicOrSlug: string;
}

export function EventLandingPage({
  eventIdPublicOrSlug,
}: EventLandingPageProps): React.JSX.Element | null {
  if (eventIdPublicOrSlug === "ltf2026") {
    return <Ltf2026OnboardingPage />;
  }

  return <GenericEventLandingPage eventIdPublicOrSlug={eventIdPublicOrSlug} />;
}

function GenericEventLandingPage({
  eventIdPublicOrSlug,
}: EventLandingPageProps): React.JSX.Element | null {
  const { state, actions } = useEventLandingController(eventIdPublicOrSlug);
  // Every branding image is a URL the host pastes; Planta never stores the
  // image, so a dead link is an ordinary case rather than an edge one. Both
  // surfaces below drop the whole credit rather than crediting an event that
  // gave us nothing to show.
  const [logoFailed, setLogoFailed] = useState(false);
  const [keyVisualFailed, setKeyVisualFailed] = useState(false);
  const [sponsorFailed, setSponsorFailed] = useState(false);
  const {
    eventName,
    logoUrl,
    keyVisualSrc,
    sponsorLogoSrc,
    sponsorName,
    participantCount,
    whatsappUrl,
    termsHref,
    privacyHref,
    isQrExpanded,
    isTvMode,
    tvHref,
    exitTvHref,
  } = state;
  const hasLogo = logoUrl !== null && !logoFailed;
  const hasSponsor = sponsorLogoSrc !== null && !sponsorFailed;

  if (state.isLoading) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-card"
        data-testid="event-landing-loading"
        role="status"
      >
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
        <span className="sr-only">
          {i18n.t("user.eventLanding.loadingLabel")}
        </span>
      </div>
    );
  }

  if (state.isError) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-card px-6"
        data-testid="event-landing-error"
      >
        <p className="text-center text-muted-foreground">
          {i18n.t("user.eventLanding.errorState.message")}
        </p>
      </div>
    );
  }

  // The venue screen: a host throws this on a projector or TV so the room can
  // scan its way in. No pointer ever touches it, so nothing here is a button.
  if (isTvMode && whatsappUrl !== null) {
    return (
      <div
        className="relative flex min-h-dvh items-center justify-center bg-primary px-[6vw] py-[6vh] text-primary-foreground"
        data-testid="event-landing-tv"
      >
        <div className="grid w-full max-w-[1600px] grid-cols-[1.15fr_1fr] items-center gap-[6vw]">
          <div className="flex flex-col">
            <PlantaLockup className="h-7 w-auto self-start" />
            <p className="mt-[5vh] text-[clamp(15px,1.5vw,22px)] font-medium text-primary-foreground/70">
              {i18n.t("user.eventLanding.eyebrow", { eventName })}
            </p>
            <h1 className="mt-4 text-pretty font-serif text-[clamp(40px,4.8vw,74px)] font-normal leading-[1.12]">
              {i18n.t("user.eventLanding.headline")}
            </h1>
            <p className="mt-5 max-w-[24ch] text-pretty text-[clamp(17px,1.8vw,27px)] leading-[1.5] text-primary-foreground/75">
              {participantCount !== null
                ? i18n.t("user.eventLanding.ledeWithCount", {
                    count: participantCount,
                  })
                : i18n.t("user.eventLanding.lede")}
            </p>
            <TvCredits
              organizedByLabel={i18n.t("user.eventLanding.organizedBy")}
              supportedByLabel={i18n.t("user.eventLanding.supportedBy")}
              eventName={eventName}
              logoUrl={hasLogo ? logoUrl : null}
              onLogoError={() => {
                setLogoFailed(true);
              }}
              sponsorLogoSrc={hasSponsor ? sponsorLogoSrc : null}
              sponsorName={sponsorName}
              onSponsorError={() => {
                setSponsorFailed(true);
              }}
            />
          </div>
          <div className="flex flex-col items-center gap-[3vh]">
            <div className="w-[min(52vh,540px)] rounded-3xl bg-white p-[4%]">
              <QRCodeSVG
                value={whatsappUrl}
                size={512}
                className="h-auto w-full"
                aria-hidden
              />
            </div>
            <p className="text-center text-[clamp(15px,1.6vw,24px)] font-medium text-primary-foreground/80">
              {i18n.t("user.eventLanding.tv.caption")}
            </p>
          </div>
        </div>
        <a
          href={exitTvHref}
          className="absolute right-6 top-5 flex items-center gap-1.5 text-[12.5px] font-medium text-primary-foreground/60"
        >
          <Minimize className="size-4 shrink-0" />
          {i18n.t("user.eventLanding.tv.exit")}
        </a>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-dvh flex-col items-center bg-card md:justify-center-safe lg:bg-background lg:pt-8 lg:pb-4"
      data-testid="event-landing-page"
    >
      {whatsappUrl !== null && (
        <a
          href={tvHref}
          className="absolute right-6 top-5 hidden items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground lg:flex"
        >
          <Maximize className="size-4 shrink-0" />
          {i18n.t("user.eventLanding.tv.present")}
        </a>
      )}
      <div className="flex w-full flex-1 flex-col md:flex-none md:max-w-[390px] lg:max-w-[920px] lg:overflow-hidden lg:rounded-2xl lg:border lg:border-border lg:bg-card lg:shadow-pds-low">
        <EventKeyVisual
          src={keyVisualFailed ? null : keyVisualSrc}
          onError={() => {
            setKeyVisualFailed(true);
          }}
        />
        <div
          className={
            whatsappUrl !== null
              ? "flex flex-1 flex-col lg:grid lg:grid-cols-[1.25fr_1fr]"
              : "flex flex-1 flex-col"
          }
        >
          <main className="mx-auto flex w-full max-w-[390px] flex-1 flex-col px-8 pb-8 pt-8 lg:max-w-none lg:px-12 lg:pb-5 max-lg:[@media(max-height:740px)]:pt-5 max-lg:[@media(max-height:740px)]:pb-4">
            <PlantaLockup className="h-5 w-auto self-start text-primary" />
            {/* The event name is the header's subject, so it stays with the
              lockup rather than drifting down with the centred group — on a
              tall phone that drift opened a 120px gap between the two. */}
            <p className="mt-2.5 text-[16px] font-medium leading-tight text-primary [@media(max-height:740px)]:mt-2">
              {i18n.t("user.eventLanding.eyebrow", { eventName })}
            </p>

            {/* Everything below the event name is one group, centred in the
              space the header and the foot leave. -safe so a column taller
              than the viewport falls back to start instead of clipping its
              own top. */}
            <div className="flex flex-1 flex-col justify-center-safe">
              {/* Planta never sets the serif bold — h1's UA bold must be undone. */}
              <h1 className="mt-3.5 text-pretty font-serif text-[30px] font-normal leading-9 text-foreground lg:max-w-[360px] lg:text-[34px] lg:leading-10 [@media(max-height:740px)]:mt-2.5 max-lg:[@media(max-height:740px)]:text-[26px] max-lg:[@media(max-height:740px)]:leading-8">
                {i18n.t("user.eventLanding.headline")}
              </h1>

              <p className="mt-3.5 text-[15px] leading-[1.5] text-muted-foreground lg:max-w-[360px] [@media(max-height:740px)]:mt-2.5">
                {participantCount !== null
                  ? i18n.t("user.eventLanding.ledeWithCount", {
                      count: participantCount,
                    })
                  : i18n.t("user.eventLanding.lede")}
              </p>

              <div
                className="event-landing-faces mt-3.5 flex items-center [@media(max-height:740px)]:mt-2.5"
                aria-hidden="true"
              >
                {FACE_SRCS.map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    className="-mr-[9px] size-[30px] rounded-full object-cover ring-2 ring-card"
                  />
                ))}
                {participantCount !== null && (
                  <span
                    data-testid="event-landing-faces-count"
                    className="flex size-[30px] shrink-0 items-center justify-center rounded-full border border-border bg-background text-[9px] font-semibold text-primary ring-2 ring-card"
                  >
                    {i18n.t("user.eventLanding.facesMore", {
                      count: participantCount,
                    })}
                  </span>
                )}
              </div>

              <ol className="my-4 divide-y divide-border lg:mb-0 lg:max-w-[380px] max-lg:[@media(max-height:740px)]:my-3">
                {STEP_KEYS.map((stepKey, index) => (
                  <li
                    key={stepKey}
                    className="flex items-start gap-3.5 py-4 [@media(max-height:740px)]:py-2.5"
                  >
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-[11.5px] font-semibold text-primary">
                      {index + 1}
                    </span>
                    <p className="text-[15.5px] leading-[1.5] text-foreground">
                      {i18n.t(stepKey)}
                    </p>
                  </li>
                ))}
              </ol>

              <div className="flex flex-col items-center gap-2 lg:hidden [@media(max-height:740px)]:gap-1">
                {whatsappUrl !== null && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="event-landing-whatsapp-cta"
                    className="flex h-13 w-full items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-medium text-primary-foreground"
                  >
                    {/* The glyph sits ~17px in a 24px slot, optically matched to
                    the label's cap height. */}
                    <span className="flex size-6 shrink-0 items-center justify-center">
                      <WhatsAppGlyph className="size-[17px]" />
                    </span>
                    {i18n.t("user.eventLanding.cta.openWhatsapp")}
                  </a>
                )}

                <p className="max-w-[281px] text-pretty text-center text-[11.5px] leading-[1.5] text-muted-foreground">
                  {i18n.t("user.eventLanding.legal.intro")}
                  <br />
                  {renderLegalBody({
                    privacyHref,
                    termsHref,
                  })}
                </p>
              </div>

              {/* A phone cannot scan its own screen, so the hand-off only
                makes sense on a window too narrow for the desktop QR panel,
                which starts at lg. */}
              {whatsappUrl !== null && (
                <div className="mt-4 hidden flex-col items-center gap-2 md:flex lg:hidden">
                  <button
                    type="button"
                    onClick={actions.toggleQrExpanded}
                    aria-expanded={isQrExpanded}
                    aria-controls="event-landing-qr-panel"
                    className="flex min-h-11 items-center justify-center gap-1.5 px-3 text-center text-[12.5px] font-medium text-muted-foreground"
                  >
                    {i18n.t("user.eventLanding.cta.scanQr")}
                    <ChevronDown
                      className={`size-4 shrink-0 ${
                        isQrExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {/* Sibling of the toggle, not a child: a tap on the code while
                  someone lines up a scan must not collapse it. */}
                  {isQrExpanded && (
                    <div
                      id="event-landing-qr-panel"
                      className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-white p-4"
                    >
                      {/* Pure white behind the QR — scanners need the contrast
                      the card ground doesn't guarantee. */}
                      <QRCodeSVG value={whatsappUrl} size={220} aria-hidden />
                      <p className="text-[12.5px] font-medium text-muted-foreground">
                        {i18n.t("user.eventLanding.qrCaption")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </main>

          {/* The desktop's one real path to Porter is the phone in your pocket,
            so the QR is the panel, not a toggle — the WhatsApp Web pattern. */}
          {whatsappUrl !== null && (
            <aside className="hidden border-border lg:flex lg:flex-col lg:items-center lg:justify-center lg:gap-4 lg:border-l lg:px-11 lg:py-12 [@media(max-height:740px)]:py-8">
              <p className="text-[14px] font-semibold text-foreground">
                {i18n.t("user.eventLanding.desktopQr.title")}
              </p>
              <div className="rounded-2xl border border-border bg-white p-4">
                <QRCodeSVG value={whatsappUrl} size={196} aria-hidden />
              </div>
              <p className="max-w-[220px] text-center text-[11.5px] leading-[1.5] text-muted-foreground">
                {i18n.t("user.eventLanding.desktopQr.caption")}
              </p>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-landing-whatsapp-desktop-cta"
                className="flex h-13 w-full items-center justify-center gap-2 rounded-full bg-primary text-[14px] font-medium text-primary-foreground"
              >
                <span className="flex size-6 shrink-0 items-center justify-center">
                  <WhatsAppGlyph className="size-[17px]" />
                </span>
                {i18n.t("user.eventLanding.desktopQr.fallback")}
              </a>
            </aside>
          )}
        </div>

        <JoinFoot
          privacyHref={privacyHref}
          termsHref={termsHref}
          eventName={eventName}
          logoUrl={logoUrl}
          hasLogo={hasLogo}
          onLogoError={() => {
            setLogoFailed(true);
          }}
          sponsorLogoSrc={sponsorLogoSrc}
          sponsorName={sponsorName}
          hasSponsor={hasSponsor}
          onSponsorError={() => {
            setSponsorFailed(true);
          }}
        />
      </div>
    </div>
  );
}
